import { isSimdSupported } from './is-simd-supported';
import JitsiStreamBackgroundEffect, {
  type IBackgroundEffectOptions,
} from './JitsiStreamBackgroundEffect';
import { showWarningNotification, timeout } from './util';
import createTFLiteModule from './vendor/tflite/tflite';
import createTFLiteSIMDModule from './vendor/tflite/tflite-simd';
const models = {
  modelLandscape:
    'https://cdn.cometchat-staging.com/calls/v5/selfie_segmentation_landscape.tflite',
};

let modelBuffer: ArrayBuffer;
let tflite: any;
let isWasmDisabled = false;

const segmentationDimensions = {
  modelLandscape: {
    height: 144,
    width: 256,
  },
};

/**
 * Creates a new instance of JitsiStreamBackgroundEffect. This loads the Meet background model that is used to
 * extract person segmentation.
 *
 * @param {Object} virtualBackground - The virtual object that contains the background image source and
 * the isVirtualBackground flag that indicates if virtual image is activated.
 * @param {Function} dispatch - The Redux dispatch function.
 * @returns {Promise<JitsiStreamBackgroundEffect>}
 */
export async function createVirtualBackgroundEffect(
  virtualBackground: IBackgroundEffectOptions['virtualBackground'],
) {
  if (
    !MediaStreamTrack.prototype.getSettings &&
    !MediaStreamTrack.prototype.getConstraints
  ) {
    throw new Error('JitsiStreamBackgroundEffect not supported!');
  }

  if (isWasmDisabled) {
    showWarningNotification('virtualBackground.backgroundEffectError');

    return;
  }

  // Checks if WebAssembly feature is supported or enabled by/in the browser.
  // Conditional import of wasm-check package is done to prevent
  // the browser from crashing when the user opens the app.

  if (!tflite) {
    try {
      const tfliteTimeout = 10000;

      if (isSimdSupported()) {
        tflite = await timeout(tfliteTimeout, createTFLiteSIMDModule());
      } else {
        tflite = await timeout(tfliteTimeout, createTFLiteModule());
      }
    } catch (err: any) {
      if (err?.message === '408') {
        console.error('Failed to download tflite model!');
        showWarningNotification('virtualBackground.backgroundEffectError');
      } else {
        isWasmDisabled = true;
        console.error(
          'Looks like WebAssembly is disabled or not supported on this browser',
          err,
        );
        showWarningNotification('virtualBackground.webAssemblyWarning');
      }

      return;
    }
  }

  if (!modelBuffer) {
    const modelResponse = await fetch(models.modelLandscape);

    if (!modelResponse.ok) {
      throw new Error('Failed to download tflite model!');
    }

    modelBuffer = await modelResponse.arrayBuffer();

    tflite.HEAPU8.set(
      new Uint8Array(modelBuffer),
      tflite._getModelBufferMemoryOffset(),
    );

    tflite._loadModel(modelBuffer.byteLength);
  }

  const options = {
    ...segmentationDimensions.modelLandscape,
    virtualBackground,
  };

  return new JitsiStreamBackgroundEffect(tflite, options);
}
